import type { ReasonCode, SubscriptionStatus } from '../../api';

/**
 * The status block at the top of E7 (M2 §4). `reasonCode` picks the C2 text; `chip` is the
 * label shown next to it ("Paused", "Suspended"), so colour is never the only cue.
 */
export interface StatusNotice {
  readonly chip: 'paused' | 'suspended' | null;
  readonly tone: 'info' | 'neutral' | 'danger';
  /**
   * The C2 text to show. For a suspension it is the API's `suspensionReasonCode` (C1 B-D6,
   * answers Q100), e.g. `DAILY_LOSS_LIMIT_REACHED` or `CHANNEL_INACCESSIBLE`; `null` only if the
   * API ever sent a suspension without its cause, which the contract refuses (C1 AC22).
   */
  readonly reasonCode: ReasonCode | null;
}

/**
 * What E7 says about the channel's state, or `null` when it copies normally. The cause of a
 * suspension is read from the settings GET (`suspensionReasonCode`), never inferred.
 */
export function statusNotice(
  status: SubscriptionStatus,
  suspensionReasonCode: ReasonCode | null,
): StatusNotice | null {
  switch (status) {
    case 'ACTIVE':
      return null;
    case 'NOT_CONFIGURED':
      return { chip: null, tone: 'info', reasonCode: 'CHANNEL_NOT_CONFIGURED' };
    case 'PAUSED':
      return { chip: 'paused', tone: 'neutral', reasonCode: 'CHANNEL_PAUSED' };
    case 'SUSPENDED_LIMIT':
    case 'SUSPENDED_EXTERNAL':
      return { chip: 'suspended', tone: 'danger', reasonCode: suspensionReasonCode };
  }
}
